import {
  browserPdfOutput,
  type CompanyNcDetail,
  type CompanyNcList,
  type CompanyNcSource,
  type HseDetail,
  type HseListResult,
  type HseOfficeRequest,
  type HseSource,
  type ReportBrand,
} from '@felix/ui';
import { api, officeApi } from './api.js';

/** PDF başlığındaki şirket markası (ADR 0057 §7); okunamazsa yok. */
async function officeBrand(): Promise<ReportBrand | null> {
  try {
    const { branding } = await officeApi.branding();
    return branding?.name ? { name: branding.name, logo: branding.logo ?? null } : null;
  } catch {
    return null;
  }
}

const NC = '/office/company-nc';
const enc = encodeURIComponent;

/**
 * Ofisin şirket uygunsuzluğu (ADR 0062 §2): şirket verisi, bulutta yazılır; gemiye inmez.
 */
export const officeCompanyNcSource: CompanyNcSource = {
  list: () => api<CompanyNcList>(NC),
  detail: (id) => api<CompanyNcDetail>(`${NC}/${enc(id)}`),
  open: (input) => api<CompanyNcDetail>(NC, { method: 'POST', body: { input } }),
  update: (id, rowVersion, input) =>
    api<CompanyNcDetail>(`${NC}/${enc(id)}`, { method: 'PATCH', body: { rowVersion, input } }),
  addAction: (id, input) =>
    api<CompanyNcDetail>(`${NC}/${enc(id)}/actions`, { method: 'POST', body: { input } }),
  changeAction: (actionId, rowVersion, change) =>
    api<CompanyNcDetail>(`${NC}/actions/${enc(actionId)}`, {
      method: 'POST',
      body: { rowVersion, change },
    }),
  close: (id, input) =>
    api<CompanyNcDetail>(`${NC}/${enc(id)}/close`, {
      method: 'POST',
      body: {
        ...(input.note ? { note: input.note } : {}),
        ...(input.password ? { password: input.password } : {}),
      },
    }),
  reopen: (id, input) =>
    api<CompanyNcDetail>(`${NC}/${enc(id)}/reopen`, {
      method: 'POST',
      body: { reason: input.reason, ...(input.password ? { password: input.password } : {}) },
    }),
};

/**
 * Ofiste HSE raporları (ADR 0049 §7): rapor gemide yazılır; ofis filoyu görür, kapanışın ofis
 * adımına parolayla karar verir, not yazar ve gemiden rapor ister. Rapor yazma (`editor`) yok.
 */
export const officeHseSource: HseSource = {
  scope: 'web',
  list: (type, vesselId) => {
    // Tür verilmezse dört tür birlikte (HSEQ Reports; ADR 0097 §1)
    const params = new URLSearchParams(type ? { type } : {});
    if (vesselId) params.set('vesselId', vesselId);
    const query = params.toString();
    return api<HseListResult>(`/office/hse${query ? `?${query}` : ''}`);
  },
  detail: (id) => api<HseDetail>(`/office/hse/${encodeURIComponent(id)}`),
  office: {
    decide: (id, input) =>
      api<HseDetail>(`/office/hse/${encodeURIComponent(id)}/decision`, {
        method: 'POST',
        body: {
          ...(input.password ? { password: input.password } : {}),
          decision: input.decision,
          ...(input.reason ? { reason: input.reason } : {}),
          expected: input.expected,
        },
      }),
    note: (id, input) =>
      api<HseDetail>(`/office/hse/${encodeURIComponent(id)}/notes`, {
        method: 'POST',
        body: input,
      }),
    requests: async () => (await api<{ items: HseOfficeRequest[] }>('/office/hse/requests')).items,
    request: async (input) => {
      await api('/office/hse/requests', { method: 'POST', body: input });
    },
    fileUrl: (id) => `/api/office/attachments/${encodeURIComponent(id)}`,
  },
  // Çıktı (ADR 0062 §3) ve şirket uygunsuzluğu (ADR 0062 §2)
  brand: officeBrand,
  pdf: browserPdfOutput(),
  companyNc: officeCompanyNcSource,
};
